import { jsx as _jsx } from "react/jsx-runtime";
import { UPGRADE_SOURCE } from 'pricing-pages-lib/constants/QueryParams';
import { Metrics } from 'pricing-pages-ui/utils/metrics';
import { useQueryParamFromLocation } from 'pricing-pages-ui/utils/urls/queryParamUtils';
import { useSelector } from 'react-redux';
import PurchaseMotionButton from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionButton';
import { BUY_TOUCHLESS, TRIAL } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { getFirstHubSkuIdFromProductConfigurations, nonRecurringProductConfigurationsSelector, offerProductConfigurationsSelector } from '../../../selectors/ProductConfigsSelectors';
import { crmSuiteProductLevelSelector, productForSkuIdSelector, rawProductListSelector } from '../../../selectors/ProductsSelectors';
import { usePMBOverride } from './PurchaseMotionButtonOverrideHooks';
import { logTX0120Exposure, useIsTX0120Variant } from '../../../hooks/tx0120Hooks';

const setupMetricCounter = upgradeData => {
  Metrics.counter('buy-now-clicked', {
    source: upgradeData && upgradeData.uniqueId || 'PMBW'
  }).increment();
};

export const getPurchaseMotionForLevel = ({
  purchaseMotions,
  level
}) => purchaseMotions[level - 1];
export const filteredPropsForPBM = props => {
  const propsToPass = Object.assign({}, props);
  delete propsToPass.nonMerchandiseIdIdentifier;
  delete propsToPass.fallbackSeleniumIdentifier;
  delete propsToPass.queryParamTerm;
  delete propsToPass.productConfigurations;
  delete propsToPass.filterOwnedMarketingProducts;
  delete propsToPass.skipMCConfirmationPage;
  return propsToPass;
};

const PurchaseMotionButtonWrapper = props => {
  const {
    trackingSource,
    productConfigurations = [],
    offerProductConfigurations: passedOfferProductConfigurations,
    nonMerchandiseIdIdentifier,
    fallbackSeleniumIdentifier,
    queryParamTerm,
    onClick,
    skipMCConfirmationPage = false
  } = props;
  const crmSuiteProductLevel = useSelector(state => crmSuiteProductLevelSelector(state, {
    skuIds: productConfigurations.map(({
      skuId
    }) => skuId)
  }));
  const crmSuiteSeleniumId = crmSuiteProductLevel && `crm-suite-${crmSuiteProductLevel}`;
  const apiName = useSelector(state => {
    const hubSkuId = getFirstHubSkuIdFromProductConfigurations(state, {
      productConfigurations
    });
    const product = hubSkuId && productForSkuIdSelector(state, {
      skuId: hubSkuId
    });
    return product && product.apiName;
  });
  const purchaseMotion = getPurchaseMotionForLevel(props);
  const {
    isTX0120Variant,
    isEligibleForTX0120,
    isTX0120Fetched
  } = useIsTX0120Variant(apiName, purchaseMotion);
  const productIdentifier = crmSuiteSeleniumId || nonMerchandiseIdIdentifier || apiName || fallbackSeleniumIdentifier;
  const offerProductConfigurations = useSelector(state => passedOfferProductConfigurations || offerProductConfigurationsSelector(state, {
    productConfigurations,
    queryParamTerm
  }));
  const nonRecurringProductConfigurations = useSelector(state => nonRecurringProductConfigurationsSelector(state, {
    productConfigurations
  }));
  const rawProductList = useSelector(rawProductListSelector);
  const propsToPass = Object.assign({}, props, {
    nonRecurringProductConfigurations,
    offerProductConfigurations,
    'data-selenium-id': `${trackingSource}-${productIdentifier}-${purchaseMotion}-button`,
    nonMerchandiseIdIdentifier: crmSuiteSeleniumId || nonMerchandiseIdIdentifier,
    truncate: true,
    'data-reagan-primary-cta': true,
    shouldShowErrorAlert: true,
    products: rawProductList
  }, purchaseMotion === BUY_TOUCHLESS && {
    onClick: e => {
      if (onClick) {
        onClick(e);
      }

      setupMetricCounter(propsToPass.upgradeData);
    }
  }, {
    passthroughProps: {
      [TRIAL]: {
        isTX0120Variant,
        isEligibleForTX0120,
        isTX0120Fetched,
        onClick: e => {
          if (onClick) {
            onClick(e);
          }

          logTX0120Exposure();
        }
      }
    }
  });
  const upgradeSource = useQueryParamFromLocation(UPGRADE_SOURCE);

  if (upgradeSource) {
    propsToPass.upgradeData = Object.assign({}, propsToPass.upgradeData, {
      source: upgradeSource
    });
  }

  return usePMBOverride(propsToPass, skipMCConfirmationPage) || /*#__PURE__*/_jsx(PurchaseMotionButton, Object.assign({}, filteredPropsForPBM(propsToPass)));
};

export default PurchaseMotionButtonWrapper;